import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Drawer,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  IconButton,
  Link,
  Box,
} from "@material-ui/core";
import FormatIndentDecreaseIcon from "@material-ui/icons/FormatIndentIncrease";
import { useTranslate } from "react-polyglot";
import { useHistory, useLocation } from "react-router-dom";
import { makeDefaultTitle } from "../../../../../Utils/misc.js";
import { useCourseProvider } from "../../../../../Providers/Data/CourseProvider.jsx";
import { useLearnProcess } from "../../../../../Providers/Data/LearnProcessProvider.jsx";
import { useBaseData } from "../../../../../Providers/Data/BaseDataProvider.jsx";
import { makeTheoryTreeItems } from "../../Theory.jsx";
import { IS_APP_BEHAPPY } from "../../../../../constants.js";
import icCheck from "../../../../../assets/ic_check_green.svg";
import icPlus from "../../../../../assets/ic_plus_pink.svg";
import { useAuth } from "../../../../../Providers/Auth/AuthProvider.jsx";

// Имя панели аккордеона теории (не должно совпадать с именами тегов упражнений)
const THEORY_ACCORDION_PANEL = "theory-accordion";

// Параметры повторных попыток прокрутки: контент вкладки теории (страницы pdf)
// подгружается асинхронно, поэтому целевой блок может сдвигаться, пока не отрисуется
const SCROLL_RETRY_INTERVAL_MS = 200;
const SCROLL_MAX_ATTEMPTS = 15;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 240,
    flexShrink: 0,
    [theme.breakpoints.up("lg")]: {
      // На больших экранах корень Drawer растягивается на всю высоту контента урока,
      // чтобы панель дерева упражнений (paper) внутри него могла "прилипать" к краю окна при прокрутке страницы.
      // pointerEvents: none нужен, чтобы прозрачный корень не перехватывал клики
      // (например, по кнопке открытия дерева слева от контента)
      position: "absolute",
      top: 0,
      bottom: 0,
      left: -270,
      width: 260,
      pointerEvents: "none",
    },
  },
  drawerPaper: {
    backgroundColor: "#fff",
    borderRadius: 10,
    width: 260,
    height: "calc(100vh - 137px)",
    overflowY: "auto",
    marginTop: 125,
    [theme.breakpoints.up("lg")]: {
      // Панель дерева упражнений прилипает к верхнему краю окна и остаётся видимой при прокрутке страницы.
      // pointerEvents: auto возвращает реакции на клики самой панели, т.к. у корня Drawer они отключены
      position: "sticky !important",
      top: 221,
      height: "calc(100vh - 226px) !important",
      marginTop: 0,
      pointerEvents: "auto",
    },
    "& .MuiAccordion-root.Mui-expanded": {
      margin: 0,
    },
    "& .MuiAccordionSummary-root.Mui-expanded": {
      backgroundColor: "#f5f5f5",
    },
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
    },
    "& .MuiAccordionDetails-root": {
      alignItems: "center",
      padding: 8,
    },
    "& .MuiLink-underlineHover": {
      cursor: "pointer",
      opacity: 0.7,
      "&:hover": {
        textDecoration: "none",
        opacity: 1,
      },
    },
  },
  buttonClose: {
    margin: "8px 0",
    width: 50,
    height: 50,
  },
  titleText: {
    textTransform: "uppercase",
    marginLeft: 5,
  },
}));

/** */
const ExercisesTreeByTags = ({ open, setOpen }) => {
  const classes = useStyles({ open });
  const history = useHistory();
  const location = useLocation();
  const t = useTranslate();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState("");
  const [currentDeckProgress, setCurrentDeckProgress] = useState(null);
  const { currentDeck, exercisesTreeForDeck } = useCourseProvider();
  const { exercise: currentExercise } = useLearnProcess();
  const { branches } = useBaseData();
  const exam = currentExercise?.deck.exam;
  // Флаг просмотра результатов сданного экзамена передаётся через location.state.
  // Сохраняем его, чтобы при переключении между упражнениями в дереве не потерять контекст
  // и не инициировать повторный старт экзамена на сервере.
  const { isViewExamDeck } = location.state || {};

  // currentDeckProgress необходим для индикации выученного упражнения, обновляется после каждого ответа на упражнение
  useEffect(() => {
    if (branches && currentDeck && currentExercise) {
      const branch = branches.find((branch) => branch.decks.find((deck) => deck.id === currentDeck.id));
      setCurrentDeckProgress(branch.decks.find((deck) => deck.id === currentDeck.id));
    } else {
      setCurrentDeckProgress(null);
    }
  }, [branches, currentExercise, currentDeck]);

  // Элементы списка теории: html-текст (всегда первым, если он есть) и pdf-файлы.
  // Аккордеон теории показывается, только когда этих элементов больше одного
  // (несколько pdf или html + pdf)
  const theoryLabel = IS_APP_BEHAPPY ? t("theoryAlt") : t("theory");
  const theoryItems = makeTheoryTreeItems(currentDeck?.theory, theoryLabel);

  /**  */
  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  /**
   * @param {[Object]} exercisesOfTag
   * @param {[Number]} learnedExercises
   * @returns {string}
   */
  const makeProgressText = (exercisesOfTag, learnedExercises) => {
    const progressOfTags =
      exercisesOfTag.filter((item) => learnedExercises.includes(item.id)).length / exercisesOfTag.length;

    return `(${Math.floor(progressOfTags * 100)}%)`;
  };

  /**
   * Прокручивает страницу к якорю теории. Контент вкладки теории подгружается
   * асинхронно (вкладка может только что смонтироваться, а страницы pdf отрисовываются
   * не сразу), поэтому прокрутка повторяется, пока положение целевого блока
   * не стабилизируется. Прерывается и при вмешательстве пользователя
   * (скролл колесом/пальцем). Сама прокрутка всегда мгновенная, без анимации.
   *
   * @param {string} anchorId - идентификатор целевого блока теории.
   */
  const scrollToTheoryAnchor = (anchorId) => {
    let attempts = 0;
    let lastOffsetTop = null;

    /** */
    const stop = () => {
      clearInterval(timer);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchmove", stop);
    };

    /** */
    const scroll = () => {
      attempts += 1;

      const element = document.getElementById(anchorId);
      if (!element) {
        // Вкладка теории ещё не смонтирована после переключения — пробуем ещё раз
        if (attempts >= SCROLL_MAX_ATTEMPTS) {
          stop();
        }
        return;
      }

      // Прокручиваем страницу к началу (для html-элемента) либо к pdf-блоку.
      // Прокрутка всегда мгновенная, без анимации
      element.scrollIntoView({ behavior: "auto", block: "start" });

      // Останавливаемся, когда блок перестал сдвигаться (контент выше отрисовался)
      if (attempts >= SCROLL_MAX_ATTEMPTS || element.offsetTop === lastOffsetTop) {
        stop();
      }

      lastOffsetTop = element.offsetTop;
    };

    const timer = setInterval(scroll, SCROLL_RETRY_INTERVAL_MS);
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchmove", stop, { passive: true });
    scroll();
  };

  /**
   * Обработчик клика по элементу списка теории: переключает урок на вкладку "Теорию" и
   * жёстко (без анимации прокрутки) перемещает страницу к выбранному pdf-файлу
   * или к началу страницы для элемента html-текста.
   *
   * @param {string} anchorId - идентификатор целевого блока теории.
   */
  const onTheoryItemClick = (anchorId) => {
    // Переключаемся на вкладку "Теорию" через кастомное событие
    window.dispatchEvent(new CustomEvent("Learning.SwitchTab", { detail: { target: "theory" } }));
    scrollToTheoryAnchor(anchorId);
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="left"
      open={open}
      onClose={() => setOpen(!open)}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Box display="flex" alignItems="center">
        <IconButton color="primary" className={classes.buttonClose} onClick={() => setOpen(!open)}>
          <FormatIndentDecreaseIcon />
        </IconButton>
        <Typography className={classes.titleText}>{t("structure")}</Typography>
      </Box>
      {theoryItems.length > 1 && (
        <Accordion
          square
          expanded={expanded === THEORY_ACCORDION_PANEL}
          onChange={handleChange(THEORY_ACCORDION_PANEL)}
        >
          <AccordionSummary>
            <Typography style={{ marginLeft: 40 }}>{theoryLabel}</Typography>
          </AccordionSummary>
          {theoryItems.map((item) => (
            <AccordionDetails key={item.anchorId}>
              <Link
                color="inherit"
                variant="body1"
                style={{ marginLeft: 16 }}
                onClick={() => onTheoryItemClick(item.anchorId)}
              >
                <Typography>{item.title}</Typography>
              </Link>
            </AccordionDetails>
          ))}
        </Accordion>
      )}
      {exercisesTreeForDeck.map((tag) => (
        <Accordion key={tag.id} square expanded={expanded === tag.name} onChange={handleChange(tag.name)}>
          <AccordionSummary>
            {tag.image && <img src={tag.image.url} alt="Tags image" width={24} height={24} />}
            <Typography style={{ marginLeft: tag.image ? 16 : 40 }}>{tag.name || t("exercises")}</Typography>
            {currentDeckProgress && (
              <Typography style={{ marginLeft: 4, fontSize: "0.9rem" }}>
                {makeProgressText(tag.exercises, currentDeckProgress.exercises.learnedItems)}
              </Typography>
            )}
          </AccordionSummary>
          {tag.exercises.map((exercise) => {
            const learnedItem =
              currentDeckProgress && currentDeckProgress.exercises.learnedItems.find((id) => id === exercise.id);
            const passedItem = currentDeckProgress?.exercises.passedItems?.includes(exercise.id);
            return (
              <AccordionDetails key={exercise.id}>
                {learnedItem && (!exam || exam.endDate !== null || user?.companyId === 4472) && (
                  <img width={12} height={12} src={icCheck} alt="" />
                )}
                {/* Для компании Persona Education показываем иконку "плюс" (повёрнутый) у упражнений в экзамене, которые ученик прошёл, но не сдал */}
                {user?.companyId === 4472 && exam && !learnedItem && passedItem && (
                  <img width={12} height={12} src={icPlus} alt="" style={{ transform: "rotate(45deg)" }} />
                )}
                <Link
                  color="inherit"
                  variant="body1"
                  style={{ marginLeft: exercise.learned ? 4 : 16 }}
                  onClick={() => {
                    history.push({
                      pathname: `/learn/${currentDeck.id}/exercise/${exercise.id}`,
                      state: { isViewExamDeck },
                    });
                    // Переключаемся на вкладку "Упражнения" в ClassicDeckContent через кастомное событие,
                    // так как URL меняется в пределах того же урока и авто-выбор вкладки по маршруту не срабатывает
                    window.dispatchEvent(new CustomEvent("Learning.SwitchTab", { detail: { target: "exercises" } }));
                  }}
                >
                  {/* В уроке-экзамене зачёркиваем названия упражнений, на которые ученик уже дал ответ в текущей итерации сдачи экзамена */}
                  <Typography
                    style={{
                      color: currentExercise?.exercise.id === exercise.id ? "black" : "inherit",
                      opacity: currentExercise?.exercise.id === exercise.id ? 1 : "inherit",
                      textDecoration: exam?.passedExercises?.includes(exercise.id) ? "line-through" : "none",
                    }}
                  >
                    {exercise.title || t(makeDefaultTitle(exercise.type, exercise.answerType))}
                  </Typography>
                </Link>
              </AccordionDetails>
            );
          })}
        </Accordion>
      ))}
    </Drawer>
  );
};
ExercisesTreeByTags.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
};

export default ExercisesTreeByTags;
